import React, { useState, useEffect } from "react";

const LoanAmortizationCalculator = () => {
  const [loanAmount, setLoanAmount] = useState(2000000);
  const [interestRate, setInterestRate] = useState(8.5);
  const [loanTenure, setLoanTenure] = useState(20);
  const [tenureType, setTenureType] = useState("years");
  const [prepayment, setPrepayment] = useState(0);
  const [prepaymentFrequency, setPrepaymentFrequency] = useState("yearly");
  const [result, setResult] = useState({
    emi: "0.00",
    totalPayment: "0.00",
    totalInterest: "0.00",
    principalAmount: "0.00",
    interestPercentage: "0",
    principalPercentage: "0",
  });
  const [schedule, setSchedule] = useState([]);
  const [showFullSchedule, setShowFullSchedule] = useState(false);

  // Amortization Calculation Function
  const calculateAmortization = () => {
    const P = parseFloat(loanAmount);
    const annual_rate = parseFloat(interestRate) / 100;
    const r = annual_rate / 12;
    let tenure = parseFloat(loanTenure);
    const prepay = parseFloat(prepayment);

    if (isNaN(P) || isNaN(r) || isNaN(tenure) || P <= 0 || tenure <= 0) return;

    const n = tenureType === "years" ? tenure * 12 : tenure;

    let emi;
    if (r > 0) {
      emi = (P * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
    } else {
      emi = P / n;
    }

    let totalInterest = 0;
    let balance = P;
    let monthsPaid = 0;
    const prepayMonthly = prepaymentFrequency === "monthly" ? prepay : 
                         prepaymentFrequency === "yearly" ? prepay / 12 : 0;
    
    const scheduleData = [];
    
    for (let i = 1; i <= n && balance > 0; i++) {
      const interestPayment = balance * r;
      const principalPayment = Math.min(emi - interestPayment, balance);
      
      balance -= principalPayment;
      totalInterest += interestPayment;
      monthsPaid++;

      let prepaymentAmount = 0;
      if (prepaymentFrequency === "monthly" && prepayMonthly > 0) {
        prepaymentAmount = prepayMonthly;
        balance -= prepayMonthly;
      } else if (prepaymentFrequency === "yearly" && i % 12 === 0 && prepay > 0) {
        prepaymentAmount = prepay;
        balance -= prepay;
      }

      if (balance < 0) balance = 0;

      scheduleData.push({
        month: i,
        emi: emi,
        principal: principalPayment,
        interest: interestPayment,
        prepayment: prepaymentAmount,
        balance: Math.max(balance, 0)
      });

      if (balance <= 0) break;
    }

    const totalPayment = (emi * monthsPaid) + (prepaymentFrequency === "monthly" ? prepayMonthly * monthsPaid : 
                        prepaymentFrequency === "yearly" ? prepay * Math.floor(monthsPaid / 12) : 0);
    
    const interestPercentage = ((totalInterest / totalPayment) * 100);
    const principalPercentage = ((P / totalPayment) * 100);

    setResult({
      emi: emi.toFixed(2),
      totalPayment: totalPayment.toFixed(2),
      totalInterest: totalInterest.toFixed(2),
      principalAmount: P.toFixed(2),
      interestPercentage: interestPercentage.toFixed(1),
      principalPercentage: principalPercentage.toFixed(1),
    });

    setSchedule(scheduleData);
  };

  useEffect(() => {
  calculateAmortization();
}, [loanAmount, interestRate, loanTenure, tenureType, prepayment, prepaymentFrequency]);

  const tenureLabel = tenureType === "years" ? "Years" : "Months";
  const displaySchedule = showFullSchedule ? schedule : schedule.slice(0, 12);

  return (
    <div className="calculator-wrapper">
      <div className="calculator-container">
        {/* Header */}
        <div className="calculator-header">
          <div className="header-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: 'white'}}>
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
              <line x1="16" y1="2" x2="16" y2="6"></line>
              <line x1="8" y1="2" x2="8" y2="6"></line>
              <line x1="3" y1="10" x2="21" y2="10"></line>
            </svg>
          </div>
          <h1>Loan Amortization Schedule Calculator</h1>
          <p>
            View detailed month-by-month breakdown of your loan payments including principal, interest, and remaining balance
          </p>
        </div>

        {/* Main Calculator Card */}
        <div className="calculator-card">
          <div className="calculator-grid">
            {/* Left Side - Input Section */}
            <div className="input-section">
              <h2 className="section-title">
                <svg className="section-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 12V8H6a2 2 0 0 1-2-2c0-1.1.9-2 2-2h12v4"></path>
                  <path d="M4 6v12c0 1.1.9 2 2 2h14v-4"></path>
                  <path d="M18 12a2 2 0 0 0-2 2c0 1.1.9 2 2 2h4v-4h-4z"></path>
                </svg>
                Loan Details
              </h2>

              {/* Loan Amount */}
              <div className="input-group">
                <label>Loan Amount</label>
                <div className="input-wrapper">
                  <span className="input-prefix">₹</span>
                  <input
                    type="number"
                    value={loanAmount}
                    onChange={(e) => setLoanAmount(e.target.value)}
                    className="calc-input"
                    placeholder="2000000"
                  />
                </div>
                <div className="quick-select-group">
                  {[500000, 1000000, 2000000, 5000000].map((amount) => (
                    <button
                      key={amount}
                      onClick={() => setLoanAmount(amount)}
                      className="quick-select-btn primary"
                    >
                      ₹{(amount / 100000).toFixed(0)}L
                    </button>
                  ))}
                </div>
              </div>

              {/* Interest Rate */}
              <div className="input-group">
                <label>Interest Rate (Annual)</label>
                <div className="input-wrapper">
                  <input
                    type="number"
                    value={interestRate}
                    onChange={(e) => setInterestRate(e.target.value)}
                    className="calc-input"
                    placeholder="8.5"
                    step="0.1"
                  />
                  <span className="input-suffix">%</span>
                </div>
                <div className="quick-select-group">
                  {[7, 8.5, 10, 12].map((rate) => (
                    <button
                      key={rate}
                      onClick={() => setInterestRate(rate)}
                      className="quick-select-btn secondary"
                    >
                      {rate}%
                    </button>
                  ))}
                </div>
              </div>

              {/* Loan Tenure */}
              <div className="input-group">
                <label>Loan Tenure</label>
                <div className="input-wrapper">
                  <input
                    type="number"
                    value={loanTenure}
                    onChange={(e) => setLoanTenure(e.target.value)}
                    className="calc-input"
                    placeholder="20"
                  />
                </div>
                <div className="quick-select-group">
                  {tenureType === "years" 
                    ? [5, 10, 15, 20, 30].map((years) => (
                        <button
                          key={years}
                          onClick={() => setLoanTenure(years)}
                          className="quick-select-btn success"
                        >
                          {years}Y
                        </button>
                      ))
                    : [12, 24, 36, 60].map((months) => (
                        <button
                          key={months}
                          onClick={() => setLoanTenure(months)}
                          className="quick-select-btn success"
                        >
                          {months}M
                        </button>
                      ))
                  }
                </div>
              </div>

              {/* Tenure Type */}
              <div className="input-group">
                <label>Tenure Type</label>
                <div className="quick-select-group">
                  {[
                    { value: "years", label: "Years" },
                    { value: "months", label: "Months" }
                  ].map((type) => (
                    <button
                      key={type.value}
                      onClick={() => {
                        setTenureType(type.value);
                        setLoanTenure(type.value === "years" ? 20 : 240);
                      }}
                      className={`quick-select-btn ${tenureType === type.value ? 'primary' : 'secondary'}`}
                    >
                      {type.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Prepayment Amount */}
              <div className="input-group">
                <label>Prepayment Amount (Optional)</label>
                <div className="input-wrapper">
                  <span className="input-prefix">₹</span>
                  <input
                    type="number"
                    value={prepayment}
                    onChange={(e) => setPrepayment(e.target.value)}
                    className="calc-input"
                    placeholder="0"
                  />
                </div>
                <div className="quick-select-group">
                  {[0, 10000, 25000, 50000].map((amount) => (
                    <button
                      key={amount}
                      onClick={() => setPrepayment(amount)}
                      className="quick-select-btn secondary"
                    >
                      {amount === 0 ? "₹0" : `₹${(amount / 1000).toFixed(0)}K`}
                    </button>
                  ))}
                </div>
              </div>

              {/* Prepayment Frequency */}
              {prepayment > 0 && (
                <div className="input-group">
                  <label>Prepayment Frequency</label>
                  <div className="quick-select-group">
                    {[
                      { value: "monthly", label: "Monthly" },
                      { value: "yearly", label: "Yearly" }
                    ].map((freq) => (
                      <button
                        key={freq.value}
                        onClick={() => setPrepaymentFrequency(freq.value)}
                        className={`quick-select-btn ${prepaymentFrequency === freq.value ? 'primary' : 'secondary'}`}
                      >
                        {freq.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <button onClick={calculateAmortization} className="calculate-btn">
                Generate Schedule
              </button>
            </div>

            {/* Right Side - Results Section */}
            <div className="result-section">
              <h2 className="section-title">
                <svg className="section-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <circle cx="12" cy="12" r="6"></circle>
                  <circle cx="12" cy="12" r="2"></circle>
                </svg>
                Payment Summary
              </h2>

              <div className="result-card">
                {/* Monthly Payment - Highlighted */}
                <div className="result-item highlight">
                  <div className="result-item-header">
                    <span className="result-label">Monthly Payment</span>
                    <div className="result-icon-wrapper white">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: 'white'}}>
                        <circle cx="12" cy="12" r="10"></circle>
                        <circle cx="12" cy="12" r="6"></circle>
                        <circle cx="12" cy="12" r="2"></circle>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    ₹{Number(result.emi).toLocaleString("en-IN")}
                  </div>
                  <div className="result-subtext">
                    Payable for {loanTenure} {tenureLabel.toLowerCase()}
                  </div>
                </div>

                {/* Principal Amount */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Principal Amount</span>
                    <div className="result-icon-wrapper blue">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#3b82f6'}}>
                        <path d="M20 12V8H6a2 2 0 0 1-2-2c0-1.1.9-2 2-2h12v4"></path>
                        <path d="M4 6v12c0 1.1.9 2 2 2h14v-4"></path>
                        <path d="M18 12a2 2 0 0 0-2 2c0 1.1.9 2 2 2h4v-4h-4z"></path>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    ₹{Number(result.principalAmount).toLocaleString("en-IN")}
                  </div>
                  <div className="result-subtext">
                    Original loan amount
                  </div>
                </div>

                {/* Total Interest */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Total Interest</span>
                    <div className="result-icon-wrapper purple">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#9333ea'}}>
                        <polyline points="22 7 13.5 15.5 8.5 10.5 2 17"></polyline>
                        <polyline points="16 7 22 7 22 13"></polyline>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    ₹{Number(result.totalInterest).toLocaleString("en-IN")}
                  </div>
                  <div className="result-subtext">
                    Interest paid over tenure
                  </div>
                </div>

                {/* Total Payment */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Total Amount Payable</span>
                    <div className="result-icon-wrapper green">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#10b981'}}>
                        <line x1="12" y1="1" x2="12" y2="23"></line>
                        <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    ₹{Number(result.totalPayment).toLocaleString("en-IN")}
                  </div>
                  <div className="result-subtext">
                    Principal + Interest {prepayment > 0 ? "+ Prepayment" : ""}
                  </div>
                </div>

                {/* Visual Breakdown */}
                <div className="breakdown-section">
                  <h3 className="breakdown-title">Payment Breakdown</h3>
                  <div className="breakdown-item">
                    <div className="breakdown-header">
                      <span className="breakdown-label">Principal Amount</span>
                      <span className="breakdown-percentage">
                        {result.principalPercentage}%
                      </span>
                    </div>
                    <div className="breakdown-bar">
                      <div
                        className="breakdown-bar-fill blue"
                        style={{
                          width: `${result.principalPercentage}%`,
                        }}
                      />
                    </div>
                  </div>
                  <div className="breakdown-item">
                    <div className="breakdown-header">
                      <span className="breakdown-label">Total Interest</span>
                      <span className="breakdown-percentage">
                        {result.interestPercentage}%
                      </span>
                    </div>
                    <div className="breakdown-bar">
                      <div
                        className="breakdown-bar-fill green"
                        style={{
                          width: `${result.interestPercentage}%`,
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Amortization Schedule Table */}
        {schedule.length > 0 && (
          <div className="calculator-card" style={{marginTop: '2rem'}}>
            <h2 className="section-title">
              <svg className="section-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                <line x1="16" y1="2" x2="16" y2="6"></line>
                <line x1="8" y1="2" x2="8" y2="6"></line>
                <line x1="3" y1="10" x2="21" y2="10"></line>
              </svg>
              Monthly Amortization Schedule
            </h2>
            
            <div style={{overflowX: 'auto', marginTop: '1.5rem'}}>
              <table style={{width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem'}}>
                <thead>
                  <tr style={{backgroundColor: '#f3f4f6', borderBottom: '2px solid #e5e7eb'}}>
                    <th style={{padding: '0.75rem', textAlign: 'left', fontWeight: '600'}}>Month</th>
                    <th style={{padding: '0.75rem', textAlign: 'right', fontWeight: '600'}}>Payment</th>
                    <th style={{padding: '0.75rem', textAlign: 'right', fontWeight: '600'}}>Principal</th>
                    <th style={{padding: '0.75rem', textAlign: 'right', fontWeight: '600'}}>Interest</th>
                    {prepayment > 0 && <th style={{padding: '0.75rem', textAlign: 'right', fontWeight: '600'}}>Prepayment</th>}
                    <th style={{padding: '0.75rem', textAlign: 'right', fontWeight: '600'}}>Balance</th>
                  </tr>
                </thead>
                <tbody>
                  {displaySchedule.map((row, index) => (
                    <tr key={index} style={{borderBottom: '1px solid #e5e7eb', backgroundColor: index % 2 === 0 ? 'white' : '#f9fafb'}}>
                      <td style={{padding: '0.75rem', fontWeight: '500'}}>{row.month}</td>
                      <td style={{padding: '0.75rem', textAlign: 'right'}}>₹{row.emi.toLocaleString('en-IN', {maximumFractionDigits: 0})}</td>
                      <td style={{padding: '0.75rem', textAlign: 'right', color: '#3b82f6'}}>₹{row.principal.toLocaleString('en-IN', {maximumFractionDigits: 0})}</td>
                      <td style={{padding: '0.75rem', textAlign: 'right', color: '#9333ea'}}>₹{row.interest.toLocaleString('en-IN', {maximumFractionDigits: 0})}</td>
                      {prepayment > 0 && <td style={{padding: '0.75rem', textAlign: 'right', color: '#10b981'}}>₹{row.prepayment.toLocaleString('en-IN', {maximumFractionDigits: 0})}</td>}
                      <td style={{padding: '0.75rem', textAlign: 'right', fontWeight: '600'}}>₹{row.balance.toLocaleString('en-IN', {maximumFractionDigits: 0})}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {schedule.length > 12 && (
              <div style={{marginTop: '1rem', textAlign: 'center'}}>
                <button 
                  onClick={() => setShowFullSchedule(!showFullSchedule)}
                  className="quick-select-btn primary"
                  style={{padding: '0.75rem 2rem'}}
                >
                  {showFullSchedule ? 'Show Less' : `Show All ${schedule.length} Months`}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Info Section */}
        <div className="info-section">
          <div className="info-header">
            <div className="info-icon-wrapper">
              <svg className="info-icon" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="16" x2="12" y2="12"></line>
                <line x1="12" y1="8" x2="12.01" y2="8"></line>
              </svg>
            </div>
            <div>
              <h3>What is a Loan Amortization Schedule?</h3>
              <p>
                A loan amortization schedule is a complete table showing every periodic payment on a loan over time. 
                Each payment is broken down to show how much goes toward the principal balance versus interest charges. 
                In the early months of a loan, most of your payment goes toward interest because you owe more principal. 
                As you pay down the loan, more of each payment is applied to the principal. This schedule helps you 
                understand exactly how your loan will be paid off and how much interest you'll pay over the life of the loan.
              </p>
            </div>
          </div>

          <div className="formula-section">
            <h4>Payment Calculation Formula:</h4>
            <div className="formula-display">
              EMI = [P × r × (1 + r)^n] / [(1 + r)^n - 1]
            </div>

            <div className="formula-params">
              <div className="formula-param">
                <div className="formula-param-key">EMI</div>
                <div className="formula-param-value">Monthly Payment Amount</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">P</div>
                <div className="formula-param-value">Principal Loan Amount</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">r</div>
                <div className="formula-param-value">Monthly Interest Rate (Annual/12)</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">n</div>
                <div className="formula-param-value">Total Number of Payments</div>
              </div>
            </div>
          </div>

          {/* FAQ Section */}
          <div className="faq-section">
            <h3>Frequently Asked Questions</h3>

            <div className="faq-item">
              <h5>How does loan amortization work?</h5>
              <p>
                Loan amortization is the process of paying off debt through regular installments over time. Each 
                payment includes both principal (the original loan amount) and interest (the cost of borrowing). 
                With each payment, you reduce the outstanding balance, which means future interest charges decrease 
                since interest is calculated on the remaining balance. This creates a predictable repayment schedule 
                where you know exactly when your loan will be paid off and how much you'll pay in total.
              </p>
            </div>

            <div className="faq-item">
              <h5>Why does more of my payment go toward interest initially?</h5>
              <p>
                Since interest is calculated on your remaining loan balance, you pay more interest at the beginning 
                when your balance is highest. As you make payments and reduce the principal, the interest portion 
                decreases while the principal portion increases, even though your total payment stays the same. This 
                is why making extra principal payments early in the loan has such a significant impact - you reduce 
                the base amount on which future interest is calculated, saving you money over the life of the loan.
              </p>
            </div>

            <div className="faq-item">
              <h5>What's the benefit of viewing an amortization schedule?</h5>
              <p>
                An amortization schedule gives you complete transparency into your loan repayment. You can see exactly 
                how much principal and interest you're paying each month, when you'll reach certain payoff milestones, 
                and your remaining balance at any point. This helps with financial planning, tax preparation (interest 
                may be deductible), understanding the impact of prepayments, and deciding whether to refinance. It also 
                helps you verify that your lender is applying payments correctly.
              </p>
            </div>

            <div className="faq-item">
              <h5>How do prepayments affect my amortization schedule?</h5>
              <p>
                Prepayments directly reduce your principal balance, which has two major benefits: you pay less total 
                interest and you can pay off your loan faster. When you make an extra payment, specify that it should 
                go toward principal (not your next regular payment). This immediately reduces the balance used to 
                calculate future interest charges. Even small regular prepayments can significantly shorten your loan 
                term and save thousands in interest. Check your loan agreement for prepayment penalties before making 
                extra payments.
              </p>
            </div>

            <div className="faq-item">
              <h5>What's the difference between amortized and interest-only loans?</h5>
              <p>
                Amortized loans have payments that include both principal and interest, so your balance decreases 
                with each payment until the loan is fully paid off. Interest-only loans require you to pay only the 
                interest charges for a specified period, with no principal reduction - your balance stays the same. 
                After the interest-only period ends, you must either pay a large lump sum or start making much higher 
                payments that include principal. Most home, car, and personal loans are amortized because they provide 
                a clear path to being debt-free.
              </p>
            </div>

            <div className="faq-item">
              <h5>Can I change my amortization schedule after taking the loan?</h5>
              <p>
                Your original amortization schedule is fixed based on your loan terms, but you can effectively modify 
                it through refinancing or prepayments. Refinancing creates a new loan with a new schedule - useful if 
                rates drop or you want to change your loan term. Making prepayments doesn't change your required monthly 
                payment, but it shortens your payoff timeline and reduces total interest. Some lenders offer loan 
                modifications or recasting (recalculating payments based on your reduced balance) for a fee, which can 
                lower your required monthly payment.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoanAmortizationCalculator;